/**
 * MobileSectionsAccordion — progressive-disclosure wrapper for the landing
 * page. On viewports below 768px it collapses each child section into an
 * accordion (one open at a time) so the user isn't overwhelmed by an endless
 * scroll. On desktop it's a transparent pass-through that renders children
 * exactly as before.
 *
 * Usage:
 *   <MobileSectionsAccordion defaultOpen="how">
 *     <MobileSectionItem
 *       id="how"
 *       eyebrow="Step-by-step"
 *       title="How AdmissionCrafts works"
 *       preview="Three quick steps from percentile to shortlist."
 *     >
 *       <HowItWorks />
 *     </MobileSectionItem>
 *     ...
 *   </MobileSectionsAccordion>
 *
 * Design notes:
 *   - Uses Radix Accordion under the hood → keyboard + screen-reader friendly.
 *   - `type="single"` + `collapsible` → only one open at a time and clicking
 *     an open trigger closes it (matches iOS Settings / Google Play mental
 *     model that mobile users already know).
 *   - Header row is ≥64px tall → easy Fitts's-law tap target.
 *   - Chevron rotates 180° on open, respecting `prefers-reduced-motion`.
 *   - Section content mounts even when closed (Radix does this natively for
 *     screen readers) → no janky lazy-mount flash on expand.
 */
import { useState, Children, isValidElement } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import useIsMobile from "@/hooks/useIsMobile";

export function MobileSectionsAccordion({ children, defaultOpen = "" }) {
  const isMobile = useIsMobile();
  const [value, setValue] = useState(defaultOpen);

  if (!isMobile) {
    // Desktop → strip the accordion wrapper, render each section as before.
    return (
      <>
        {Children.map(children, (child) => {
          if (!isValidElement(child)) return child;
          // MobileSectionItem's `children` are the real section components.
          return child.props?.children ?? null;
        })}
      </>
    );
  }

  return (
    <AccordionPrimitive.Root
      type="single"
      collapsible
      value={value}
      onValueChange={setValue}
      className="bg-slate-50/70 border-t border-b border-slate-200 divide-y divide-slate-200"
      data-testid="mobile-sections-accordion"
    >
      {children}
    </AccordionPrimitive.Root>
  );
}

export function MobileSectionItem({ id, eyebrow, title, preview, chip, children }) {
  const isMobile = useIsMobile();

  if (!isMobile) {
    // Desktop path is handled by the parent (strips the wrapper).
    return null;
  }

  return (
    <AccordionPrimitive.Item value={id} data-testid={`mobile-section-${id}`}>
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          className="group flex w-full items-center gap-3 px-4 py-4 text-left bg-white hover:bg-amber-50/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
          data-testid={`mobile-section-trigger-${id}`}
        >
          <div className="flex-1 min-w-0">
            {eyebrow ? (
              <p className="text-[10px] tracking-[0.22em] uppercase font-semibold text-amber-700">
                {eyebrow}
              </p>
            ) : null}
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <h2 className="font-display font-bold text-slate-900 text-[17px] leading-tight tracking-tight">
                {title}
              </h2>
              {chip ? (
                <span
                  className="inline-flex items-center gap-1 px-2 h-[22px] rounded-full bg-slate-900/[0.06] text-slate-600 text-[10.5px] font-semibold tabular-nums tracking-[0.02em] whitespace-nowrap group-data-[state=open]:bg-amber-100 group-data-[state=open]:text-amber-800"
                  data-testid={`mobile-section-chip-${id}`}
                >
                  {chip}
                </span>
              ) : null}
            </div>
            {preview ? (
              <p className="mt-1 text-[12.5px] text-slate-500 leading-snug line-clamp-2">
                {preview}
              </p>
            ) : null}
          </div>
          <span className="flex-shrink-0 w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center transition-transform duration-200 group-data-[state=open]:rotate-180 group-data-[state=open]:border-amber-300 group-data-[state=open]:bg-amber-50">
            <ChevronDown className="w-4 h-4 text-slate-500 group-data-[state=open]:text-amber-700" />
          </span>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content
        className="overflow-hidden bg-white data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
        data-testid={`mobile-section-content-${id}`}
      >
        {/* Reset section padding on mobile — inner components already have
            their own top/bottom padding, which we shrink via a scoped style
            override so accordion content feels tight, not double-padded. */}
        <div className="[&_section]:!py-6 [&_section]:border-0 [&>*:first-child_section:first-child]:!pt-2 pb-2">
          {children}
        </div>
        {/* Bottom "collapse" affordance — mirrors the header trigger so
            users don't have to scroll all the way back up to close a long
            expanded section (Fitts's Law + reduces re-scroll fatigue). */}
        <AccordionPrimitive.Header className="flex border-t border-slate-100">
          <AccordionPrimitive.Trigger
            className="group flex-1 flex items-center justify-center gap-2 h-12 text-[12.5px] font-semibold tracking-[0.02em] text-slate-500 hover:text-amber-700 hover:bg-amber-50/40 active:bg-amber-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            data-testid={`mobile-section-close-${id}`}
            aria-label={`Collapse ${title}`}
          >
            <ChevronDown className="w-4 h-4 rotate-180 transition-transform" />
            <span>Collapse section</span>
          </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
}

export default MobileSectionsAccordion;
